/*
  Our application forms, filed straight into Nimble.

  Each field carries data-nimble-key, the field's name in the Nimble form.
  On submit the values go to the same public endpoint Nimble's embedded
  form posts to, as { response_values: { key: { value } } }, so the entry
  lands in that Nimble form with its usual mapping. No API key is involved.

  In the theme editor the form also reads the Nimble form's definition and
  lists any block whose key Nimble does not know, and any required Nimble
  field with no block, so a renamed field is caught before it costs entries.
*/
(function () {
  if (customElements.get('sm-nimble-form')) return;

  const API = 'https://app.nimble.com/api/v1/webforms';

  class SmNimbleForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-nimble-form]');
      if (!this.form || this.form.dataset.ready) return;
      this.form.dataset.ready = '';
      // Drawn for preview before the Nimble form exists: nothing to send to.
      this.unconnected = !this.dataset.companyId || !this.dataset.formId;
      this.endpoint = `${API}/${encodeURIComponent(this.dataset.companyId)}/${encodeURIComponent(this.dataset.formId)}`;
      this.form.addEventListener('submit', (e) => this.submit(e));
      if (this.hasAttribute('data-check-fields') && !this.unconnected) this.checkFields();
    }

    fields() {
      return Array.from(this.form.querySelectorAll('[data-nimble-key]'));
    }

    setBusy(busy) {
      const button = this.form.querySelector('[data-nimble-submit]');
      const label = this.form.querySelector('[data-nimble-submit-label]');
      if (!button || !label) return;
      if (!this.idleLabel) this.idleLabel = label.textContent;
      button.disabled = busy;
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
      label.textContent = busy ? this.dataset.sendingLabel || this.idleLabel : this.idleLabel;
    }

    showError(message) {
      const box = this.form.querySelector('[data-nimble-error]');
      const text = this.form.querySelector('[data-nimble-error-text]');
      if (!box || !text) return;
      text.textContent = message || this.dataset.errorMessage || '';
      box.hidden = false;
    }

    clearError() {
      const box = this.form.querySelector('[data-nimble-error]');
      if (box) box.hidden = true;
    }

    showSuccess() {
      const success = this.querySelector('[data-nimble-success]');
      this.form.hidden = true;
      if (!success) return;
      success.hidden = false;
      success.focus({ preventScroll: true });
      success.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    async submit(event) {
      event.preventDefault();
      if (this.unconnected) return;
      this.clearError();

      // The browser's own checks (required, email format) with its messages.
      if (!this.form.checkValidity()) {
        this.form.classList.add('is-validated');
        this.form.reportValidity();
        return;
      }

      // A filled-in trap field means a bot. Pretend it worked and send nothing.
      const trap = this.form.querySelector('[data-nimble-trap]');
      if (trap && trap.value) {
        this.showSuccess();
        return;
      }

      const values = {};
      this.fields().forEach((field) => {
        const key = field.dataset.nimbleKey;
        if (!key) return;
        const value = field.value.trim();
        values[key] = { value: value || null };
      });

      this.setBusy(true);
      try {
        const res = await fetch(`${this.endpoint}/responses`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ response_values: values }),
        });
        if (res.ok) {
          this.showSuccess();
          return;
        }
        let message = '';
        try {
          const data = await res.json();
          message = data && data.human_readable_message;
        } catch (e) {
          // No readable body; fall back to our own message.
        }
        this.showError(message);
      } catch (e) {
        this.showError();
      } finally {
        this.setBusy(false);
      }
    }

    async checkFields() {
      const box = this.querySelector('[data-nimble-check]');
      if (!box) return;
      let definition;
      try {
        const res = await fetch(this.endpoint, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(res.status);
        definition = await res.json();
      } catch (e) {
        box.textContent = 'Editor check: could not read this form from Nimble. Check the company and form IDs.';
        box.hidden = false;
        return;
      }

      const live = (definition.fields || [])
        .filter((f) => !f.archived_at && f.field_definition)
        .map((f) => f.field_definition);
      const known = new Set(live.map((f) => f.field_name));
      const ours = new Set(this.fields().map((f) => f.dataset.nimbleKey).filter(Boolean));

      const problems = [];
      this.fields().forEach((f) => {
        const key = f.dataset.nimbleKey;
        if (!key) problems.push('A field block has no Nimble field key.');
        else if (!known.has(key)) problems.push(`"${key}" is not a field in this Nimble form.`);
      });
      live.forEach((f) => {
        if (f.required && f.field_type !== 'hidden' && !ours.has(f.field_name)) {
          problems.push(`Nimble requires "${f.label}" (key ${f.field_name}) and this form has no block for it.`);
        }
      });

      const title = definition.form_name ? `Editor check (${definition.form_name}): ` : 'Editor check: ';
      box.textContent = problems.length ? title + problems.join(' ') : `${title}every field matches Nimble.`;
      box.classList.toggle('is-ok', !problems.length);
      box.hidden = false;
    }
  }

  customElements.define('sm-nimble-form', SmNimbleForm);
})();
